import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ShopifyOrderDetail.css';

const ShopifyOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/shopify/order/${id}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      alert('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status, type) => {
    const statusClasses = {
      financial: {
        paid: 'badge-success',
        pending: 'badge-warning',
        refunded: 'badge-danger',
        partially_paid: 'badge-info',
        partially_refunded: 'badge-info'
      },
      fulfillment: {
        fulfilled: 'badge-success',
        partial: 'badge-warning',
        null: 'badge-secondary'
      }
    };

    const className = type === 'financial'
      ? statusClasses.financial[status] || 'badge-secondary'
      : statusClasses.fulfillment[status] || 'badge-secondary';

    return <span className={`badge ${className}`}>{status || 'unfulfilled'}</span>;
  };

  if (loading) {
    return (
      <div className="order-detail-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-detail-container">
        <div className="error-message">
          <h2>Order not found</h2>
          <button onClick={() => navigate('/shopify/orders')} className="btn btn-primary">
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="order-detail-container">
      <div className="order-detail-header">
        <button onClick={() => navigate('/shopify/orders')} className="back-button">
          ← Back to Orders
        </button>
        <h1>Order #{order.order_number}</h1>
      </div>

      <div className="order-detail-content">
        <div className="order-info-grid">
          <div className="info-section">
            <h2>Order Information</h2>
            <div className="info-row">
              <span className="label">Order Number:</span>
              <span className="value">#{order.order_number}</span>
            </div>
            <div className="info-row">
              <span className="label">Date:</span>
              <span className="value">{formatDate(order.created_at)}</span>
            </div>
            <div className="info-row">
              <span className="label">Payment Status:</span>
              <span className="value">
                {getStatusBadge(order.financial_status, 'financial')}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Fulfillment Status:</span>
              <span className="value">
                {getStatusBadge(order.fulfillment_status, 'fulfillment')}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Total:</span>
              <span className="value total">{formatCurrency(order.total_price)}</span>
            </div>
            {order.tags && (
              <div className="info-row">
                <span className="label">Tags:</span>
                <span className="value">{order.tags}</span>
              </div>
            )}
            {order.note && (
              <div className="info-row">
                <span className="label">Note:</span>
                <span className="value">{order.note}</span>
              </div>
            )}
          </div>

          <div className="info-section">
            <h2>Customer Information</h2>
            <div className="info-row">
              <span className="label">Name:</span>
              <span className="value">
                {order.customer_first_name} {order.customer_last_name}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Email:</span>
              <span className="value">{order.email || '-'}</span>
            </div>

            {order.shipping_address && (
              <>
                <h3>Shipping Address</h3>
                <div className="address">
                  {order.shipping_address.name && <div>{order.shipping_address.name}</div>}
                  {order.shipping_address.company && <div>{order.shipping_address.company}</div>}
                  {order.shipping_address.address1 && <div>{order.shipping_address.address1}</div>}
                  {order.shipping_address.address2 && <div>{order.shipping_address.address2}</div>}
                  {order.shipping_address.city && (
                    <div>
                      {order.shipping_address.city}, {order.shipping_address.province_code} {order.shipping_address.zip}
                    </div>
                  )}
                  {order.shipping_address.country && <div>{order.shipping_address.country}</div>}
                  {order.shipping_address.phone && <div>Phone: {order.shipping_address.phone}</div>}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="line-items-section">
          <h2>Line Items</h2>
          <table className="line-items-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>SKU</th>
                <th>Quantity</th>
                <th>Unit Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {order.line_items && order.line_items.length > 0 ? (
                order.line_items.map((item, index) => (
                  <tr key={item.id || index}>
                    <td>
                      <div className="product-info">
                        <strong>{item.name || item.title}</strong>
                        {item.variant_title && (
                          <div className="variant-title">{item.variant_title}</div>
                        )}
                        {item.properties && item.properties.length > 0 && (
                          <div className="properties">
                            {item.properties.map((prop, idx) => (
                              <div key={idx}>
                                {prop.name}: {prop.value}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>{item.sku || '-'}</td>
                    <td>{item.quantity}</td>
                    <td>{formatCurrency(item.price)}</td>
                    <td>{formatCurrency(item.price * item.quantity)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="no-items">No line items found</td>
                </tr>
              )}
            </tbody>
            {order.line_items && order.line_items.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan="4" className="text-right"><strong>Subtotal:</strong></td>
                  <td><strong>{formatCurrency(
                    order.line_items.reduce((sum, item) => sum + (parseFloat(item.price) * item.quantity), 0)
                  )}</strong></td>
                </tr>
                {order.shipping_lines && order.shipping_lines.length > 0 && (
                  <>
                    {order.shipping_lines.map((shipping, index) => (
                      <tr key={index}>
                        <td colSpan="4" className="text-right">
                          Shipping ({shipping.title}):
                        </td>
                        <td>{formatCurrency(shipping.price)}</td>
                      </tr>
                    ))}
                  </>
                )}
                <tr className="total-row">
                  <td colSpan="4" className="text-right"><strong>Total:</strong></td>
                  <td><strong>{formatCurrency(order.total_price)}</strong></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {order.shipping_lines && order.shipping_lines.length > 0 && (
          <div className="shipping-section">
            <h2>Shipping Information</h2>
            {order.shipping_lines.map((shipping, index) => (
              <div key={index} className="shipping-method">
                <div className="info-row">
                  <span className="label">Method:</span>
                  <span className="value">{shipping.title}</span>
                </div>
                <div className="info-row">
                  <span className="label">Price:</span>
                  <span className="value">{formatCurrency(shipping.price)}</span>
                </div>
                {shipping.carrier_identifier && (
                  <div className="info-row">
                    <span className="label">Carrier:</span>
                    <span className="value">{shipping.carrier_identifier}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ShopifyOrderDetail;